import React, { useEffect, useState } from 'react'

import {
  badgeConfigurationDefaultFields,
  badgesSubjectsApplication,
  possibleBadgeConfigurationFields
} from 'constants/index'
import { Box, IconButton, Stack, styled } from '@mui/material'
import { Formik, Form, Field } from 'formik'
import { Button, Typography, Grid, Checkbox, Switch, makeStyles } from '@material-ui/core'
import ClearIcon from '@mui/icons-material/Clear'
import CustomTextField from 'components/Form/CustomTextField'
import { addBadgeConfigurationSchema } from 'schemas/badges'
import badgesSlice, {
  addBadgeConfiguration,
  getBadgeConfiguration,
  updateBadgeConfiguration
} from 'store/badges'
import { useDispatch, useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import moment from 'moment-timezone'
import Wrapper from 'components/Wrapper'
import ColorPicker from 'material-ui-color-picker'

export const FormErrorMessage = styled(Typography)({
  color: '#FF0000',
  fontSize: '12px',
  marginLeft: '10px'
})

const ConfigurationForm = (props) => {
  const { title, configurationId, onClose } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const { conference } = useSelector((state) => state.conference)
  const { badgeConfiguration } = useSelector((state) => state.badges)

  const [addingConfiguration, setAddingConfiguration] = useState(false)
  const [loadingConfiguration, setLoadingConfiguration] = useState(false)

  useEffect(() => {
    const getData = async () => {
      setLoadingConfiguration(true)
      await dispatch(getBadgeConfiguration(configurationId, conference?.id))
      setLoadingConfiguration(false)
    }
    if (configurationId && conference?.id) {
      getData()
    }
    return () => {
      dispatch(badgesSlice.actions.setBadgeConfiguration(null))
    }
  }, [configurationId, conference?.id])

  const initialValues = {
    title: badgeConfiguration?.title ?? '',
    badgeBarColor: badgeConfiguration?.badgeBarColor ?? '#000000',
    fieldsAppliedInsideQRCode: badgeConfiguration?.fieldsAppliedInsideQRCode ?? [],
    fieldsAppliedInsideBadge: badgeConfiguration?.fieldsAppliedInsideBadge ?? [],
    applicableSubjects: badgeConfiguration?.applicableSubjects ?? []
  }

  const handleSubmit = async (values) => {
    setAddingConfiguration(true)
    if (configurationId) {
      await updateBadgeConfiguration(
        configurationId,
        values,
        conference?.id,
        () => {
          setAddingConfiguration(false)
          onClose()
        },
        () => {
          setAddingConfiguration(false)
        }
      )
    } else {
      await addBadgeConfiguration(
        { ...values, createdAt: moment().utc().valueOf() },
        conference?.id,
        () => {
          setAddingConfiguration(false)
          onClose()
        },
        () => {
          setAddingConfiguration(false)
        }
      )
    }
  }

  return (
    <Box>
      <Stack direction="row" className={classes.header}>
        <Typography variant="h5">{title}</Typography>
        <IconButton className={classes.iconButton} onClick={onClose} disabled={addingConfiguration}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Wrapper inProgress={loadingConfiguration}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={addBadgeConfigurationSchema}
          onSubmit={handleSubmit}>
          {({ errors, values, setFieldValue }) => (
            <Form className={classes.form}>
              <Grid container alignItems="center" spacing={4}>
                <Grid item xs={12} md={4} lg={4}>
                  <CustomTextField label="Configuration Title" name="title" type="text" />
                </Grid>

                <Grid item xs={12} md={2}>
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{ backgroundColor: values.badgeBarColor }}
                      className={classes.colorPicker}
                    />
                    <ColorPicker
                      className={classes.colorInput}
                      fullWidth
                      variant="outlined"
                      label="Badge Bar Color"
                      value={values.badgeBarColor}
                      defaultValue={values.badgeBarColor}
                      onChange={(color) => setFieldValue('badgeBarColor', color)}
                    />
                  </div>
                  <FormErrorMessage>{errors.badgeBarColor}</FormErrorMessage>
                </Grid>

                <Grid item xs={12} md={6} lg={6}>
                  <Box sx={{}}>
                    <Typography className={classes.appliesTo}>Applies to:</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                      {badgesSubjectsApplication.map((e) => (
                        <label key={e} className={classes.checkboxLabel}>
                          {e}
                          <Field as={Switch} name="applicableSubjects" type="checkbox" value={e} />
                        </label>
                      ))}
                    </Box>
                    <FormErrorMessage>{errors.applicableSubjects}</FormErrorMessage>
                  </Box>
                </Grid>
              </Grid>

              <Stack direction="row" sx={{ justifyContent: 'center', marginTop: '1.5rem' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography className={classes.sectionHeading}>Field Names</Typography>
                  {badgeConfigurationDefaultFields.map(({ key, value }) => (
                    <label
                      key={key}
                      className={classes.borderedValue}
                      style={{ paddingLeft: '10px', borderLeft: '1px solid #E0E0E0' }}>
                      {value}
                    </label>
                  ))}
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography className={classes.sectionHeading}>Encode in QR Code</Typography>
                    {badgeConfigurationDefaultFields.map(({ key }) => (
                      <Field
                        key={key}
                        className={[classes.checkbox, classes.borderedValue]}
                        as={Checkbox}
                        name="fieldsAppliedInsideQRCode"
                        type="checkbox"
                        value={key}
                      />
                    ))}
                  </Box>
                  <FormErrorMessage>{errors.fieldsAppliedInsideQRCode}</FormErrorMessage>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography className={classes.sectionHeading}>Appear on Badge</Typography>
                    {possibleBadgeConfigurationFields.map(({ key }) => (
                      <Field
                        key={key}
                        className={[classes.checkbox, classes.borderedValue]}
                        as={Checkbox}
                        name="fieldsAppliedInsideBadge"
                        type="checkbox"
                        value={key}
                      />
                    ))}
                  </Box>
                  <FormErrorMessage>{errors.fieldsAppliedInsideBadge}</FormErrorMessage>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} className={classes.actions}>
                <Button size="large" onClick={onClose} disabled={addingConfiguration}>
                  Cancel
                </Button>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={addingConfiguration}
                  disabled={addingConfiguration}
                />
              </Stack>
            </Form>
          )}
        </Formik>
      </Wrapper>
    </Box>
  )
}

export default ConfigurationForm

const useStyles = makeStyles({
  colorPicker: {
    borderRadius: '8px',
    position: 'absolute',
    top: '8px',
    left: '8px',
    width: '93%',
    height: '40px',
    boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)'
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0px',
    marginBottom: '16px'
  },
  iconButton: {
    padding: '8px'
  },
  appliesTo: {
    fontSize: '15px',
    fontWeight: 'bold'
  },
  sectionHeading: {
    fontSize: '15px',
    fontWeight: 'bold',
    paddingBottom: '16px',
    borderBottom: '1px solid #E0E0E0'
  },
  defaultValue: {
    fontSize: '13px',
    padding: '9px'
  },
  checkboxLabel: {
    cursor: 'pointer'
  },
  checkbox: {
    borderRadius: '0px',
    padding: '0px',
    height: '38px'
  },
  borderedValue: {
    color: '#606060',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '15px',
    fontWeight: 'bold',
    height: '38px',
    width: '230px',
    borderBottom: '1px solid #E0E0E0',
    borderRight: '1px solid #E0E0E0'
  },
  actions: {
    justifyContent: 'end',
    margin: '16px 0px'
  }
})
